import React from 'react';
import Heading from '../ui/PageUI/Heading';
import Subheading from '../ui/PageUI/SubHeading';
import Button from '../components/Button';
import Description from '../ui/PageUI/Description';
import FeatureCard from '../ui/PageUI/FeatureCard';
import AboutImage from '../Assets/about.png';
import Paragraph from '../ui/PageUI/Paragraph';
import Intro from '../ui/PageUI/Intro';
import ImageWithText from '../ui/PageUI/ImageWithText';
import imageSrc from '../Assets/Legal.png';
import TeamIllustration from '../Assets/TeamIllustration.png';
import GlobeIllustration from '../Assets/pricing/bitcoin.png';
import { motion } from 'framer-motion';

const links = [
  { name: 'Open roles', href: '#careers', icon: '💼' },
  { name: 'Internship program', href: '#internships', icon: '🎓' },
  { name: 'Our vision', href: '#vision', icon: '🔭' },
  { name: 'Meet our leadership', href: '#leadership', icon: '👥' },
];

const stats = [
  { name: 'Liquidity Partners', value: '12', suffix: '+', icon: '🤝' },
  { name: 'Cryptocurrencies', value: '300', suffix: '+', icon: '🪙' },
  { name: 'Regulatory Licenses', value: '4', suffix: '+', icon: '🛡️' },
  { name: 'Countries Supported', value: '25', suffix: '+', icon: '🌎' },
];

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const AboutUs = () => {
  return (
    <div className="relative text-white overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20"></div>
      </div>

      {/* Hero Section */}
      <section className="relative max-w-7xl mx-auto px-6 py-24 flex flex-col md:flex-row items-center gap-12">
        <motion.div 
          className="md:w-1/2 space-y-6"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block px-3 py-1 bg-blue-900/30 items-center rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-800/50">
            About Laani
          </div>
          <Heading text="Democratizing Crypto Access" />
          <Subheading text="Simplifying digital wealth for everyone" />
          <Intro
            text="At Laani, we're breaking down the barriers to cryptocurrency investment. Our intuitive platform transforms complex blockchain technology into simple, secure tools for building your digital future—no jargon, just clarity."
          />
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button 
              text="Start Exploring" 
              onClick={() => alert('Button Clicked!')} 
              className="bg-blue-600 hover:bg-blue-700 transition-all transform hover:scale-105" 
            />
            <Button 
              text="Meet Our Team →" 
              onClick={() => window.location.hash = '#leadership'} 
              className="bg-transparent border border-gray-600 hover:border-blue-400 hover:text-blue-400" 
            />
          </div>
        </motion.div>
        <motion.div 
          className="md:w-1/2 md:pl-10 mt-8 md:mt-0"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="relative">
            <img 
              className="w-full h-auto rounded-2xl border border-gray-700/50 shadow-2xl" 
              src={AboutImage} 
              alt="Laani platform interface" 
            />
            <div className="absolute -bottom-6 -right-6 bg-gray-800/80 backdrop-blur-sm p-4 rounded-xl border border-gray-700 shadow-lg">
              <div className="text-blue-400 text-sm font-medium">300+ Assets</div>
              <div className="text-xs text-gray-400">Supported on our platform</div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="relative py-16 bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-y border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-4 gap-8"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            {stats.map((stat) => (
              <motion.div 
                key={stat.name}
                variants={fadeIn}
                className="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-blue-500/30 transition-all"
                whileHover={{ y: -5 }}
              >
                <div className="text-2xl mb-2">{stat.icon}</div>
                <dt className="text-sm text-gray-400">{stat.name}</dt>
                <dd className="text-3xl font-bold text-white mt-2">
                  {stat.value}<span className="text-blue-400">{stat.suffix}</span>
                </dd>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="relative py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-800/50">
              Our Approach
            </div>
            <Description text="Redefining crypto accessibility" className="text-3xl font-bold" />
            <p className="text-gray-400 max-w-2xl mx-auto mt-4">
              We're building more than a platform—we're creating an ecosystem that empowers every investor.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <FeatureCard
                title="Financial Literacy"
                description="Interactive learning modules, live webinars, and personalized guidance to demystify crypto investing."
                icon="📚"
                gradient="from-blue-900/30 to-blue-800/20"
              />
            </motion.div>
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
            >
              <FeatureCard
                title="Accessibility"
                description="One-click investing, intuitive dashboards, and 24/7 support—designed for all experience levels."
                icon="👐"
                gradient="from-purple-900/30 to-purple-800/20"
              />
            </motion.div>
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
            >
              <FeatureCard
                title="Wealth Building"
                description="Automated portfolio strategies, institutional-grade security, and real-time market insights."
                icon="📈"
                gradient="from-teal-900/30 to-teal-800/20"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="relative py-20 px-6 bg-gray-900/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-16">
          <div className="md:w-1/2">
            <img 
              src={GlobeIllustration} 
              alt="Laani global reach" 
              className="w-full h-auto rounded-2xl border border-gray-700/50 shadow-xl"
            />
          </div>
          <div className="md:w-1/2 space-y-6">
            <div className="inline-block px-3 py-1 bg-green-900/30 rounded-full text-green-400 text-sm font-medium mb-4 border border-green-800/50">
              Global Vision
            </div>
            <h3 className="text-3xl font-bold">Borderless Financial Access</h3>
            <p className="text-gray-400">
              Laani operates across 25+ countries with localized support, multiple language options, 
              and region-specific investment opportunities. Our technology bridges traditional finance 
              with the decentralized future.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                <div className="text-green-400 text-lg font-medium">Multi-currency</div>
                <p className="text-sm text-gray-400">Support for 15+ fiat currencies</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                <div className="text-green-400 text-lg font-medium">24/7 Support</div>
                <p className="text-sm text-gray-400">Global customer service team</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="relative py-28 px-6 bg-gradient-to-b from-blue-900/20 to-transparent ">
        <div className="max-w-7xl mx-auto">
          <ImageWithText
            heading="Trust Through Transparency"
            subheading="Security First"
            paragraph="Our compliance framework exceeds industry standards, employing bank-grade security measures while maintaining user-friendly access."
            bullets={[
              { text: 'Biometric authentication & multi-sig wallets', icon: '🔒' },
              { text: 'Regular third-party security audits', icon: '📋' },
              { text: 'Real-time transaction monitoring', icon: '👁️' },
              { text: 'Insurance-backed digital asset protection', icon: '🛡️' },
            ]}
            imageUrl={imageSrc}
            ctaText="Explore Our Security"
            ctaLink="/security"
            reverseLayout={true}
          />
        </div>
      </section>

      {/* Team Section */}
      <section id="leadership" className="relative py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-purple-900/30 rounded-full text-purple-400 text-sm font-medium mb-4 border border-purple-800/50">
              Our Culture
            </div>
            <h2 className="text-3xl font-bold mb-4">The Minds Behind Laani</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A diverse team of fintech innovators, blockchain engineers, and financial experts united by a shared vision.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <img 
                src={TeamIllustration} 
                alt="Laani team" 
                className="w-full h-auto rounded-2xl border border-gray-700/50 shadow-xl"
              />
            </div>
            <div className="md:w-1/2 space-y-6">
              <h3 className="text-2xl font-bold">Building the Future Together</h3>
              <p className="text-gray-400">
                We believe the best products come from collaborative environments that value transparency, 
                continuous learning, and user-centric design. Our flat hierarchy encourages innovation at 
                every level.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                  <div className="text-purple-400 text-lg font-medium">45+ Team Members</div>
                  <p className="text-sm text-gray-400">Across 12 countries</p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700/50">
                  <div className="text-purple-400 text-lg font-medium">Open Dialogue</div>
                  <p className="text-sm text-gray-400">Weekly AMAs with leadership</p>
                </div>
              </div>
              <Button 
                text="View Open Positions →" 
                onClick={() => window.location.hash = '#careers'} 
                className="mt-6 bg-transparent border border-purple-600 text-purple-400 hover:bg-purple-900/30" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="relative py-28 px-6 bg-gradient-to-b from-blue-900/20 to-transparent">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-block px-3 py-1 bg-blue-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-800/50">
            Our North Star
          </div>
          <Paragraph
            heading="Democratizing Wealth Creation"
            text="We envision a world where geographic borders and financial gatekeepers no longer limit economic opportunity. By 2030, we aim to onboard 1 billion people to the crypto economy through education, intuitive products, and community empowerment."
            centered={true}
          />
          <div className="mt-12">
            <Button 
              text="Read Our 2030 Vision →" 
              onClick={() => window.location.href = '/vision'} 
              className="bg-transparent border border-blue-500 text-blue-400 hover:bg-blue-900/30" 
            />
          </div>
        </div>
      </section>

      {/* Links and CTA */}
      <section className="relative py-20 px-6 ">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-b from-blue-900/20 to-transparent  rounded-2xl border border-gray-700/50 p-12 text-center">
            <h2 className="text-3xl font-bold mb-6">Join Our Journey</h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Whether you're looking to invest, partner, or build with us, we're excited to connect.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              {links.map((link) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-700/50 rounded-full border border-gray-600 hover:border-blue-400 hover:text-blue-400 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>{link.icon}</span>
                  {link.name}
                </motion.a>
              ))}
            </div>
            
            <div className="max-w-md mx-auto">
              <Button 
                text="Get Started with Laani" 
                onClick={() => alert('Get Started Clicked!')} 
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg py-4" 
              />
              <p className="text-gray-500 text-sm mt-4">
                Trusted by over 500,000 users worldwide
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;











